import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ContactStep, ContactFormData, CONTACT_CATEGORIES } from "../types";
import Stepper from "../../../components/stepper/Stepper";
import StepFlowLayout from "../../../components/step-flow/StepFlowLayout";
import type { StepFlowAction } from "../../../components/step-flow/stepFlowActions";
import {
  StepComplete,
  StepConfirmItem,
  StepFormDescription,
  StepFormGroup,
  StepFormLabel,
  StepFormTitle,
} from "../../../components/step-flow/StepFlowFormParts";
import { completeTransition, stepTransition } from "../../../components/step-flow/stepFlowTransitions";
import Input from "../../../components/form-item/Input";
import FileUploader from "../../../components/file-uploader/FileUploader";
import Textarea from "../../../components/form-item/Textarea";
import Select, { SelectChangeEvent } from "../../../components/form-item/Select";
import Toggle from "../../../components/form-item/Toggle";
import { HOSTNAME } from "../../../constant/host";

// ===== Constants =====

const STEPS: ContactStep[] = ['form', 'confirm', 'complete'];
const STEP_LABELS = ['入力', '確認', '完了'];

const INITIAL_FORM_DATA: ContactFormData = {
  category: '',
  name: '',
  email: '',
  tel: '',
  company: '',
  content: '',
  attachment: undefined,
  agree: false,
};

// ===== Component =====

const ContactForm: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<ContactStep>('form');
  const [formData, setFormData] = useState<ContactFormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const currentStepIndex = STEPS.indexOf(currentStep);

  const handleInputChange = useCallback((field: keyof ContactFormData, value: string | boolean | File | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleNext = useCallback(() => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const handleBack = useCallback(() => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('category', formData.category);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('tel', formData.tel || '');
      formDataToSend.append('company', formData.company || '');
      formDataToSend.append('content', formData.content);
      if (formData.attachment) {
        formDataToSend.append('attachment', formData.attachment);
      }
      formDataToSend.append('agree', formData.agree ? '同意する' : '同意しない');

      const response = await fetch(`${HOSTNAME}/api/contact/submit/`, {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (!response.ok || result.status === 'error') {
        throw new Error(result.message || '送信に失敗しました');
      }

      setCurrentStep('complete');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitError(error instanceof Error ? error.message : '送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData]);

  const isFormValid = useCallback(() => {
    return (
      formData.category !== '' &&
      formData.name !== '' &&
      formData.email !== '' &&
      formData.content !== '' &&
      formData.agree === true
    );
  }, [formData]);

  const renderFormStep = () => (
    <motion.div {...stepTransition}>
      <StepFormTitle>お問い合わせ</StepFormTitle>
      <StepFormDescription>
        お問い合わせの内容に近いものをお選びください。
        <br />
        ※ まだ内容が固まっていなくても大丈夫です。
      </StepFormDescription>

      <StepFormGroup>
        <Select
          label="お問い合わせ種別"
          value={formData.category}
          onChange={(e: SelectChangeEvent) => handleInputChange('category', e.target.value)}
          options={[
            { value: '', name: '選択してください' },
            ...CONTACT_CATEGORIES.map(category => ({ value: category, name: category }))
          ]}
        />
      </StepFormGroup>

      <StepFormGroup>
        <Input
          label="氏名"
          option="required"
          type="text"
          placeholder="山田 太郎"
          value={formData.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)}
        />
      </StepFormGroup>

      <StepFormGroup>
        <Input
          label="メールアドレス"
          option="required"
          type="email"
          placeholder="example@email.com"
          value={formData.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
        />
      </StepFormGroup>

      <StepFormGroup>
        <Input
          label="電話番号"
          option="optional"
          type="tel"
          placeholder="090-1234-5678"
          value={formData.tel || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('tel', e.target.value)}
        />
      </StepFormGroup>

      <StepFormGroup>
        <Input
          label="会社名/団体名"
          option="optional"
          type="text"
          placeholder="株式会社○○"
          value={formData.company || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('company', e.target.value)}
        />
      </StepFormGroup>

      <StepFormGroup>
        <Textarea
          label="お問い合わせ内容"
          option="required"
          placeholder="お問い合わせ内容をご記入ください"
          value={formData.content}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('content', e.target.value)}
          rows={5}
        />
      </StepFormGroup>

      <StepFormGroup>
        <div className="u-col u-gap4 u-w100">
          <StepFormLabel>
            添付ファイル
            <span className="label8 u-pl8 u-text-light-gray">任意</span>
          </StepFormLabel>
          <FileUploader
            files={formData.attachment ? [formData.attachment] : []}
            onChange={(files) => {
              if (files.length > 0) {
                handleInputChange('attachment', files[0]);
              } else {
                handleInputChange('attachment', undefined);
              }
            }}
            multiple={false}
            height={120}
            placeholder="クリックまたはファイルをドラッグ & ドロップ"
          />
        </div>
      </StepFormGroup>

      <StepFormGroup>
        <Toggle
          value={formData.agree}
          onClick={() => handleInputChange('agree', !formData.agree)}
        >
          <span className="bodytext">
            <a
              href="/privacypolicy"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: 'var(--soremo-blue)', textDecoration: 'none', fontSize: 'inherit' }}
            >
              個人情報の取り扱い
            </a>
            について同意します
          </span>
        </Toggle>
      </StepFormGroup>
    </motion.div>
  );

  const renderConfirmStep = () => (
    <motion.div {...stepTransition}>
      <StepFormTitle>入力内容の確認</StepFormTitle>
      <StepFormDescription>以下の内容で送信します。よろしいですか？</StepFormDescription>

      <StepConfirmItem label="お問い合わせ種別" value={formData.category} fallback="未選択" />
      <StepConfirmItem label="氏名" value={formData.name} />
      <StepConfirmItem label="メールアドレス" value={formData.email} />
      <StepConfirmItem label="電話番号" value={formData.tel} />
      <StepConfirmItem label="会社名/団体名" value={formData.company} />
      <StepConfirmItem label="お問い合わせ内容" value={formData.content} />
      <StepConfirmItem label="添付ファイル" value={formData.attachment?.name} fallback="なし" />
      <StepConfirmItem label="個人情報の取り扱いへの同意" value={formData.agree ? '同意する' : '同意しない'} />
    </motion.div>
  );

  const renderCompleteStep = () => (
    <motion.div {...completeTransition}>
      <StepComplete title="送信が完了しました">
        お問い合わせいただきありがとうございます。
        <br />
        内容を確認の上、担当者よりご連絡いたします。
        <br />
        今しばらくお待ちください。
      </StepComplete>
    </motion.div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 'form':
        return renderFormStep();
      case 'confirm':
        return renderConfirmStep();
      case 'complete':
        return renderCompleteStep();
      default:
        return null;
    }
  };

  // フッター（StepFlowLayout）: 左=戻る / 右=次へ
  const primaryAction: StepFlowAction | undefined = (() => {
    switch (currentStep) {
      case 'form':
        return {
          label: '確認画面へ',
          onClick: handleNext,
          disabled: !isFormValid(),
          rightIcon: 'chevron_right',
        };
      case 'confirm':
        return {
          label: '送信',
          onClick: handleSubmit,
          loading: isSubmitting,
          rightIcon: 'chevron_right',
        };
      case 'complete':
        return {
          label: 'トップへ',
          onClick: () => navigate('/'),
          rightIcon: 'chevron_right',
        };
      default:
        return undefined;
    }
  })();

  const secondaryAction: StepFlowAction | undefined =
    currentStep === 'confirm'
      ? {
          label: '戻る',
          onClick: handleBack,
          disabled: isSubmitting,
          leftIcon: 'chevron_left',
        }
      : undefined;

  // 直接開かれた（履歴がない）場合はトップへ
  const handleExit = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <StepFlowLayout
      onBack={handleExit}
      ariaLabel="お問い合わせ"
      stepper={
        <Stepper
          stepLabels={STEP_LABELS}
          current={currentStepIndex + 1}
          showCheckOnComplete={true}
        />
      }
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      footerCenter={
        submitError ? (
          <span className="bodytext-13 u-text-green">{submitError}</span>
        ) : undefined
      }
    >
      <AnimatePresence mode="wait">
        {renderStepContent()}
      </AnimatePresence>
    </StepFlowLayout>
  );
};

export default ContactForm;
